import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../interceptors";
import EmptyState from "../../components/shared/EmptyState";
import LoadingState from "../../components/shared/LoadingState";
import ErrorState from "../../components/shared/ErrorState";
import { toast } from "../../components/shared/toast";

// ── /candidate/documents ───────────────────────────────────────────────────
// Backend: POST /job-posting/api/candidate-portal/documents/list returns
// DocumentDTO rows (id, documentType, documentName, s3Url, fileSizeBytes,
// mimeType, status, uploadedAt).
//
// Add + delete endpoints exist (documents/add, documents/delete) but the
// upload pipeline (pre-signed S3 PUT, type-detection, virus scan) isn't
// wired through the candidate portal yet. This page renders the list +
// download links + delete; upload UI shows a "coming soon" hint instead
// of a fake drop zone.

const TYPE_LABEL = {
  RESUME:      "Resume",
  COVER_LETTER: "Cover letter",
  ID_PROOF:    "ID proof",
  CERTIFICATE: "Certificate",
  PORTFOLIO:   "Portfolio",
  OTHER:       "Other",
};

function fmtBytes(n) {
  if (n == null || !Number.isFinite(n) || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(); } catch { return ""; }
}

export default function CandidateDocuments() {
  const candidateId = useMemo(() => {
    const raw = localStorage.getItem("candidateId") || localStorage.getItem("userId");
    return raw ? Number(raw) : null;
  }, []);

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(() => {
    if (!candidateId) {
      setLoading(false);
      setError("Not signed in as a candidate");
      return;
    }
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    axiosInstance
      .post("/job-posting/api/candidate-portal/documents/list", { candidateId })
      .then((r) => {
        if (r.data?.success) {
          setDocs(Array.isArray(r.data.data) ? r.data.data : []);
        } else {
          setError(r.data?.message || "Couldn't load documents");
          setErrorStatus(500);
        }
      })
      .catch((e) => {
        setError(e?.message || "Couldn't load documents");
        setErrorStatus(e?.status || e?.response?.status || 500);
      })
      .finally(() => setLoading(false));
  }, [candidateId]);

  useEffect(() => {
    document.title = "Documents - Ample Hire";
    load();
  }, [load]);

  const removeDoc = async (id) => {
    if (!window.confirm("Remove this document? Recruiters will no longer see it.")) return;
    setDeletingId(id);
    try {
      await axiosInstance.post("/job-posting/api/candidate-portal/documents/delete", { documentId: id });
      setDocs((prev) => prev.filter((d) => d.id !== id));
      toast.success("Document removed");
    } catch (e) {
      toast.error("Couldn't remove document", { description: e?.message });
    } finally {
      setDeletingId(null);
    }
  };

  const uploadComingSoon = () => {
    toast.info("Document upload is coming soon", {
      description: "For now, send files directly to your recruiter — they'll attach them on your behalf.",
    });
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <header>
        <h1 className="text-2xl font-semibold text-vf-base m-0">Documents</h1>
        <p className="text-sm text-vf-muted mt-1">
          Resume, cover letter, certificates — everything you've shared with recruiters.
        </p>
      </header>

      {/* Upload — disabled UI with honest "coming soon" copy */}
      <section
        aria-label="Upload"
        className="rounded-card border border-dashed border-vf-highlight bg-vf-tint/30 p-5 text-center"
      >
        <div className="text-2xl mb-2" aria-hidden="true">📂</div>
        <div className="text-sm font-semibold text-vf-base">Upload is coming soon</div>
        <p className="text-xs text-vf-muted mt-1 m-0">
          For now, share files directly with your recruiter — they can attach them on your behalf.
        </p>
        <button
          type="button"
          onClick={uploadComingSoon}
          className="mt-3 text-xs px-3 py-1.5 rounded border border-subtle bg-white text-vf-base hover:bg-white/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
        >
          Notify me when ready
        </button>
      </section>

      {/* List */}
      {loading ? (
        <div className="bg-white border border-subtle rounded-card p-4">
          <LoadingState variant="skeleton" shape="row" count={3} />
        </div>
      ) : error ? (
        <ErrorState
          status={errorStatus}
          description={errorStatus === 403 ? undefined : error}
          retry={load}
        />
      ) : docs.length === 0 ? (
        <div className="bg-white border border-subtle rounded-card">
          <EmptyState
            icon={<span aria-hidden="true">📄</span>}
            title="No documents yet"
            description="When you or your recruiter uploads a file, it'll appear here."
          />
        </div>
      ) : (
        <ul className="list-none p-0 m-0 space-y-2">
          {docs.map((d) => {
            const typeLabel = TYPE_LABEL[d.documentType] || d.documentType || "Document";
            const size = fmtBytes(d.fileSizeBytes);
            const when = fmtDate(d.uploadedAt);
            return (
              <li
                key={d.id}
                className="rounded-card border border-subtle bg-white px-4 py-3 flex items-start gap-3"
              >
                <div className="w-9 h-9 rounded bg-vf-tint flex items-center justify-center flex-shrink-0" aria-hidden="true">
                  <span className="text-lg">📄</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-vf-base truncate">
                    {d.documentName || `Document ${d.id}`}
                  </div>
                  <div className="text-2xs text-vf-muted mt-0.5 flex flex-wrap gap-x-1.5">
                    <span>{typeLabel}</span>
                    {size && <><span aria-hidden="true">·</span><span>{size}</span></>}
                    {when && <><span aria-hidden="true">·</span><span>Uploaded {when}</span></>}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-1.5 flex-shrink-0">
                  {d.s3Url && (
                    <a
                      href={d.s3Url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-2xs font-semibold px-2.5 py-1 rounded border border-subtle text-vf-base hover:bg-vf-tint focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary text-center"
                    >
                      Download
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => removeDoc(d.id)}
                    disabled={deletingId === d.id}
                    className="text-2xs font-semibold px-2.5 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary text-center"
                  >
                    {deletingId === d.id ? "Removing…" : "Remove"}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
